import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { ReactNode } from 'react';
import { MetricTabLabel, type MetricTabLabelProps } from './MetricTabLabel.tsx';
import { hiddenIndicatorSx, metricTabsRootSx, panelSx, tabSx, tabsSx } from './styles.ts';
import { useMetricTabs } from './useMetricTabs.ts';

export type { MetricTabChange } from './MetricTabLabel.tsx';

export type MetricTabItem = MetricTabLabelProps & {
  id: string;
  content: ReactNode;
};

export type MetricTabsProps = {
  tabs: MetricTabItem[];
  value: string;
  onChange: (value: string) => void;
  sx?: SxProps<Theme>;
};

export const MetricTabs = ({ tabs, value, onChange, sx = [] }: MetricTabsProps) => {
  const { getTabId, getPanelId, handleChange } = useMetricTabs({ onChange });

  return (
    <Box sx={[metricTabsRootSx, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Tabs
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        slotProps={{ indicator: { sx: hiddenIndicatorSx } }}
        sx={tabsSx}
      >
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            id={getTabId(tab.id)}
            aria-controls={getPanelId(tab.id)}
            aria-busy={tab.isLoading || undefined}
            value={tab.id}
            label={<MetricTabLabel title={tab.title} value={tab.value} change={tab.change} isLoading={tab.isLoading} />}
            sx={tabSx}
          />
        ))}
      </Tabs>
      {tabs.map(tab => {
        const isActive = tab.id === value;

        return (
          <Box
            key={tab.id}
            id={getPanelId(tab.id)}
            role="tabpanel"
            aria-labelledby={getTabId(tab.id)}
            hidden={!isActive}
            sx={panelSx}
          >
            {isActive ? tab.content : null}
          </Box>
        );
      })}
    </Box>
  );
};
